import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import Icon from '../../components/AppIcon';
import { fetchPlatformRevenueHistory, fetchPlatformRevenueByStream, fetchSystemMetrics } from '../../lib/adminApi';

const streamLabels = {
  commission: 'Commission',
  menu_markup: 'Order markup profit',
  verification_fee: 'Verification profit',
  sponsored_placement: 'Sponsored placement',
};

export default function PlatformHealthPage() {
  const [revenue, setRevenue] = useState([]);
  const [byStream, setByStream] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    async function loadData() {
      setLoading(true);
      setError(null);

      try {
        const [revenueData, streamData, metricsData] = await Promise.all([
          fetchPlatformRevenueHistory(),
          fetchPlatformRevenueByStream(),
          fetchSystemMetrics(),
        ]);
        if (isMounted) {
          setRevenue(revenueData);
          setByStream(streamData);
          setMetrics(metricsData);
        }
      } catch (err) {
        console.error('Failed to load platform data', err);
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to load platform data');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadData();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Platform Health</h2>
          <p className="text-sm text-muted-foreground">
            Track marketplace liquidity, commission intake, and trust signals.
          </p>
        </div>
        <button className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground shadow-sm hover:bg-primary/90">
          <Icon name="TrendingUp" size={16} /> Download financial packet
        </button>
      </header>

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* System Metrics */}
      {metrics && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Escrowed Balance</p>
                <p className="mt-2 text-2xl font-semibold text-foreground">R {Number(metrics.escrows.balance || 0).toLocaleString()}</p>
                <p className="mt-1 text-xs text-muted-foreground">{metrics.escrows.pending} pending</p>
              </div>
              <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-100 text-blue-600">
                <Icon name="Lock" size={22} />
              </span>
            </div>
          </div>
          <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Bids</p>
                <p className="mt-2 text-2xl font-semibold text-foreground">{metrics.bids.total}</p>
                <p className="mt-1 text-xs text-muted-foreground">{metrics.bids.accepted} accepted</p>
              </div>
              <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Icon name="FileText" size={22} />
              </span>
            </div>
          </div>
          <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Failed Payments</p>
                <p className="mt-2 text-2xl font-semibold text-foreground text-rose-600">{metrics.payments.failed}</p>
                <p className="mt-1 text-xs text-muted-foreground">{metrics.payments.total} total</p>
              </div>
              <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-rose-100 text-rose-600">
                <Icon name="AlertCircle" size={22} />
              </span>
            </div>
          </div>
          <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Platform Revenue (all streams)</p>
                <p className="mt-2 text-2xl font-semibold text-foreground text-emerald-600">R {Number(metrics.revenue || 0).toLocaleString()}</p>
                <p className="mt-1 text-xs text-muted-foreground">All time</p>
              </div>
              <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-emerald-100 text-emerald-600">
                <Icon name="TrendingUp" size={22} />
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Profit by stream: commission, markup, verification, sponsored */}
      {byStream && (
        <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-foreground">Platform profit by stream</h3>
          <p className="mt-1 text-sm text-muted-foreground">Commission made, order markup profit, verification profit, sponsored placement.</p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {Object.entries(byStream).map(([key, amount]) => (
              <div key={key} className="rounded-lg border border-border bg-muted/20 px-4 py-3">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{streamLabels[key] ?? key}</p>
                <p className="mt-1 text-xl font-semibold text-foreground">R {Number(amount || 0).toLocaleString()}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="rounded-xl border border-border bg-card shadow-sm">
          <div className="border-b border-border/80 px-5 py-4">
            <h3 className="text-lg font-semibold text-foreground">Platform Revenue (last 12 entries)</h3>
            <p className="text-xs text-muted-foreground">Commission, menu markup, verification fee, sponsored placement.</p>
          </div>
          <div className="overflow-hidden">
            <ul className="divide-y divide-border/80">
              {loading && (
                <li className="px-5 py-5 text-sm text-muted-foreground">Loading revenue data…</li>
              )}
              {!loading && revenue.length === 0 && (
                <li className="px-5 py-5 text-sm text-muted-foreground">No platform revenue entries recorded yet.</li>
              )}
              {revenue.map((entry) => (
                <li key={entry.id} className="px-5 py-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-foreground">{streamLabels[entry.revenue_stream] ?? entry.revenue_stream ?? 'Revenue'}</p>
                      <p className="text-xs text-muted-foreground">{entry.description ?? '—'}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-foreground">R{Number(entry.amount ?? 0).toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">{entry.created_at ? format(new Date(entry.created_at), 'dd MMM yyyy') : '—'}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="rounded-xl border border-border bg-card p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-foreground">Reliability Checklist</h3>
          <p className="mt-1 text-sm text-muted-foreground">Operational levers to keep the trust lane green.</p>
          <ul className="mt-5 space-y-4 text-sm text-muted-foreground">
            <li className="flex gap-3">
              <span className="mt-0.5">
                <Icon name="ShieldCheck" size={16} className="text-emerald-500" />
              </span>
              <span>Weekly escrow reconciliation across all open events.</span>
            </li>
            <li className="flex gap-3">
              <span className="mt-0.5">
                <Icon name="BarChart3" size={16} className="text-indigo-500" />
              </span>
              <span>Monitor caterer NPS and trigger success outreach below 70.</span>
            </li>
            <li className="flex gap-3">
              <span className="mt-0.5">
                <Icon name="LifeBuoy" size={16} className="text-sky-500" />
              </span>
              <span>Escalate SLA breaches to the support squad within 4 hours.</span>
            </li>
            <li className="flex gap-3">
              <span className="mt-0.5">
                <Icon name="AlertTriangle" size={16} className="text-amber-500" />
              </span>
              <span>Flag revenue dips &gt;10% week-over-week for leadership review.</span>
            </li>
          </ul>
        </section>
      </div>
    </div>
  );
}
